import { AfterViewInit, Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { DomSanitizer, SafeHtml, Title } from '@angular/platform-browser';
import { createSelector, select, Store } from '@ngrx/store';
import { Observable, Subject } from 'rxjs';
import { filter, take, takeUntil } from 'rxjs/operators';
import { AppState } from 'src/app/app.state';
import { postApiRequest } from 'src/app/core/api/api.actions';
import { ApiStatus } from 'src/app/core/api/api.models';
import { ApiService } from 'src/app/core/api/api.service';
import { PaymentPortalLoginResponse } from 'src/app/core/api/responses/payment-portal-login';
import { selectAppConfig } from 'src/app/core/application-config/application-config.selectors';
import { selectLanguage } from 'src/app/core/client-configuration/client-configuration.selectors';
import { LocaleService } from 'src/app/core/locale/locale.service';
import { showNotificationAction } from 'src/app/core/notification/notification.actions';
import { NotificationType } from 'src/app/core/notification/notification.state';
import { routeTo } from 'src/app/core/routing/routing.actions';
import { NotificationFocusService } from 'src/app/core/services/notification-focus.service';
import { showToastAction } from 'src/app/core/toast/toast.actions';
import { ToastType } from 'src/app/core/toast/toast.state';
import { setMfaCodeAction } from 'src/app/core/user/user.actions';
import { dayjs } from 'src/app/dayjs.lib';
import { getAccessoPayRedirectParams } from 'src/app/feature/accesso-pay/accesso-pay.utils';
import { DynamicFormComponent } from 'src/app/feature/dynamic-form/dynamic-form.component';
import { DynamicFormControl } from 'src/app/feature/dynamic-form/input/dynamic-form-control';
import { ApiRequestType } from 'src/app/shared/enums/api.enums';
import { Dictionary } from 'src/app/shared/types/dictionary';
import { validString } from 'src/app/shared/utilities/types.utils';
import {
	APPCONFIG_ASSET_PATH,
	APPCONFIG_LOGO,
	APPCONFIG_MFA_RESEND_CODE_WAIT_TIME,
} from '../../shared/enums/application-config.enums';
import { routeToOrdersAction } from '../orders/orders.actions';
import { PostVerifyAction, setRecoverMFATokenAction } from '../recover/recover.actions';
import { selectRecoverState } from '../recover/recover.selectors';
import { RecoverState } from '../recover/recover.state';

@Component({
	selector: 'ahp-verify',
	templateUrl: './verify.component.html',
	styleUrls: ['./verify.component.scss'],
})
export class VerifyComponent implements OnInit, AfterViewInit, OnDestroy {
	/**
	 * Dynamic Form that contains the verify input
	 */
	@ViewChild('verifyForm') verifyForm: DynamicFormComponent;
	/**
	 * If we're authenticating
	 */
	public isAuthenticating$: Observable<boolean>;
	/**
	 * If the form has been submitted.
	 */
	public verifyCodeSubmitted = false;
	/**
	 * Our verify form configuration
	 */
	public verifyFormControls: DynamicFormControl<any>[][] = [];
	/**
	 * logo path built from appConfig
	 */
	public logoPath: string;
	/**
	 * Verification Instructions Locale
	 */
	public instructionsLocale: string | SafeHtml;
	/**
	 * Active counter for resend code wait time
	 */
	public countdownToResend: number;
	/**
	 * Active countdown timer to display resend code wait time
	 */
	public countdownDisplay: string;
	/**
	 * Interval for the wait time count down
	 */
	private _resendInterval: ReturnType<typeof setInterval>;
	/**
	 * Our subject to modify the observable if we're authenticating.
	 */
	private _isAuthenticating = new Subject<boolean>();
	/**
	 * For unsubscribing to observables when the component destroys.
	 */
	private _destroy$ = new Subject<void>();
	/**
	 * Verify form data
	 */
	private _verifyFormData: Dictionary<any> = null;
	/**
	 * User email used on recover form
	 */
	private _userInfo: RecoverState;
	/**
	 * The currently configured language.
	 */
	private _language: string;
	/**
	 * MFA Code Resend Wait Time App Config value
	 */
	private _mfaCodeResendWaitTime: number;

	/**
	 * The constructor
	 * @param _sanitizer The DOM Sanitizer
	 * @param _locale Locale service
	 * @param _apiService Api Service
	 * @param _store$ Our main store
	 * @param _notificationFocusService Notification focus service
	 * @param _title Title service
	 */
	constructor(
		private _store$: Store<AppState>,
		private _sanitizer: DomSanitizer,
		private _locale: LocaleService,
		private _apiService: ApiService,
		private _notificationFocusService: NotificationFocusService,
		private _title: Title
	) {
		this._title.setTitle(_locale.get('verify.pagetitle'));
		this.isAuthenticating$ = this._isAuthenticating.asObservable();
		this._store$
			.pipe(
				select(
					createSelector(selectRecoverState, selectLanguage, (info, lang) => {
						return {
							info,
							lang,
						};
					})
				),
				take(1)
			)
			.subscribe(({ info, lang }) => {
				this._userInfo = info;
				this._language = lang;
			});
	}

	/**
	 * Handles initialization
	 * Checks for and sets the merchant logo if there is one
	 */
	public ngOnInit(): void {
		history.pushState(null, null, window.location.href);
		this.instructionsLocale = this._sanitizer.bypassSecurityTrustHtml(
			this._locale.get('verify.instructions').replace(/@@email/g, this._userInfo?.email)
		);
		this.verifyFormControls = this._getVerifyForm();
		this._store$
			.pipe(
				select(selectAppConfig([APPCONFIG_ASSET_PATH, APPCONFIG_LOGO, APPCONFIG_MFA_RESEND_CODE_WAIT_TIME])),
				takeUntil(this._destroy$)
			)
			.subscribe((appConfigs) => {
				const { assetsPath, logo } = appConfigs;
				this._mfaCodeResendWaitTime = appConfigs?.mfa_resend_code_wait_time ?? 60000;
				this._startResendWaitTimeCountdown();

				if (validString(assetsPath) && validString(logo)) {
					this.logoPath = assetsPath.concat(logo);
				}
			});

		this._apiService.onResponse$
			.pipe(
				filter(
					(resp) =>
						resp.SERVICE.request_type === ApiRequestType.PAYMENT_PORTAL_LOGIN ||
						resp.SERVICE.request_type === ApiRequestType.UNIFIED_GUEST_LOGIN
				),
				takeUntil(this._destroy$)
			)
			.subscribe((resp) => {
				if (resp.SERVICE.status === 'FAILED') {
					this._isAuthenticating.next(false);
				}
				this._onPaymentPortalResponse(resp);
			});
	}

	public ngAfterViewInit(): void {
		this._notificationFocusService.setFocusedElement(this.verifyForm.textInputs);
	}

	/**
	 * Handles cleanup when the component is being destroyed
	 */
	public ngOnDestroy(): void {
		this._destroy$.next();
		this._destroy$.complete();
		this._killCountDownInterval();
	}

	/**
	 * Called when our form data changes
	 * @param data The form data
	 */
	public verifyFormChanged(data: Dictionary<any>): void {
		this._verifyFormData = data.valid ? data : null;
	}

	/**
	 * Handles Resend Code
	 */
	public resendCode(): void {
		this._isAuthenticating.next(true);
		const payloadObject = {
			email: this._userInfo?.email,
			order_id: this._userInfo?.order_id,
			phone_number: this._userInfo?.phone,
			language: this._language,
		};

		// Order ID and Email are from Login Flow
		// oToken and eToken are from auto-login flow
		if (!payloadObject.email && !payloadObject.order_id && this._userInfo?.oToken && this._userInfo?.eToken) {
			payloadObject['oToken'] = this._userInfo?.oToken;
			payloadObject['eToken'] = this._userInfo?.eToken;
		}
		const haveOrderID = !!this._userInfo?.order_id || !!this._userInfo?.oToken;
		// If we don't have an order_id,
		// we're in the recover flow
		// and need to call GuestOrderSearch
		const requestType = haveOrderID ? ApiRequestType.UNIFIED_GUEST_LOGIN : ApiRequestType.GUEST_ORDER_SEARCH;

		this._apiService
			.post(requestType, payloadObject)
			.pipe(take(1))
			.subscribe((response) => {
				this._isAuthenticating.next(false);
				if (response.SERVICE.http_response_code === '401') {
					this._store$.dispatch(
						showToastAction({
							message: this._locale.get('verify.emailsent'),
							toastType: ToastType.SUCCESS,
						})
					);
					this._startResendWaitTimeCountdown();
				} else {
					this._store$.dispatch(
						showNotificationAction({
							buttonLabel: this._locale.get('common.close'),
							dialogType: NotificationType.GENERAL,
							initiator: `Recover: Failed`,
							message: this._locale.get(`errorcode.${response.SERVICE.error_code}`),
						})
					);
				}
			});
	}

	/**
	 * Returns our MFA Validator
	 */
	public onSubmit(): void {
		let verify;
		const { postVerifyAction, email, phone, order_id, eToken, oToken, user_token } = this._userInfo;
		this.verifyCodeSubmitted = true;

		if (!this._verifyFormData) {
			return;
		} else {
			verify = this._verifyFormData.value.verify;
		}

		this._isAuthenticating.next(true);

		switch (postVerifyAction) {
			case PostVerifyAction.ThreeDS:
				this._store$.dispatch(
					postApiRequest({
						requestType: ApiRequestType.PAYMENT_PORTAL_LOGIN,
						params: {
							eToken,
							oToken,
							user_token,
							mfa_code: verify,
						},
						customData: {
							disableRouting: true,
							threeDSParams: getAccessoPayRedirectParams(this._userInfo),
						},
					})
				);
				return;
			// Otherwise its order lookup
			case PostVerifyAction.Orders:
				this._apiService
					.post(ApiRequestType.GUEST_ORDER_SEARCH, {
						email,
						phone_number: phone,
						mfa_code: verify,
					})
					.pipe(take(1))
					.subscribe((response) => {
						const { SERVICE } = response;
						const { _locale } = this;

						this._isAuthenticating.next(false);

						if (SERVICE.status === ApiStatus.OK) {
							if (SERVICE?.MATCH?.length) {
								[
									setRecoverMFATokenAction({ mfaToken: verify }),
									setMfaCodeAction({ code: verify, expiration: new Date(SERVICE.mfa_expiration) }),
									routeToOrdersAction({ payload: response.SERVICE.MATCH }),
									routeTo({ payload: ['orders'] }),
								].forEach((action) => this._store$.dispatch(action));
							} else {
								this._store$.dispatch(
									showNotificationAction({
										initiator: '[Verify] MFA Validator 401',
										dialogType: NotificationType.GENERAL,
										buttonLabel: _locale.get('common.close'),
										message: _locale.get(`verify.noorders`),
									})
								);
							}
							return;
						} else if (SERVICE.http_response_code === '401' && this.verifyCodeSubmitted) {
							this._store$.dispatch(
								showToastAction({
									message: this._locale.get('verify.invalidcode'),
									toastType: ToastType.ERROR,
								})
							);
							this.verifyCodeSubmitted = false;
							return;
						}

						this._store$.dispatch(
							showNotificationAction({
								initiator: '[Verify] MFA Validator Error',
								dialogType: NotificationType.GENERAL,
								buttonLabel: _locale.get('common.close'),
								message: _locale.get(`errorcode.${SERVICE.error_code}`),
							})
						);
					});
				return;
			case PostVerifyAction.AutoLogin:
			case PostVerifyAction.Login:
			case PostVerifyAction.Payments:
			default:
				this._store$.dispatch(
					postApiRequest({
						requestType: ApiRequestType.UNIFIED_GUEST_LOGIN,
						params: {
							eToken,
							email,
							order_id,
							oToken,
							user_token,
							mfa_code: verify,
						},
						customData: {
							postVerifyAction: postVerifyAction as PostVerifyAction,
						},
					})
				);
		}
	}

	/**
	 * Starts the resend wait time countdown interval
	 * @private
	 */
	private _startResendWaitTimeCountdown(): void {
		// Prevent starting a new interval if one is already going
		if (this._resendInterval) {
			return;
		}

		this._killCountDownInterval();
		this.countdownToResend = this._mfaCodeResendWaitTime / 1000;
		this.countdownDisplay = dayjs.duration(this.countdownToResend, 'seconds').format('mm:ss');
		this._resendInterval = setInterval(this._doCountDownIntervalAction.bind(this), 1000);
	}

	/**
	 * Action to do on each pulse of the interval
	 * @private
	 */
	private _doCountDownIntervalAction(): void {
		this.countdownToResend = --this.countdownToResend;
		this.countdownDisplay = dayjs.duration(this.countdownToResend, 'seconds').format('mm:ss');

		if (this.countdownToResend <= 0) {
			this._killCountDownInterval();
		}
	}

	/**
	 * Ensure the interval is properly killed off
	 * @private
	 */
	private _killCountDownInterval(): void {
		clearInterval(this._resendInterval);
		this._resendInterval = null;
		this.countdownDisplay = null;
		this.countdownToResend = 0;
	}

	/**
	 * Returns the Verify Form layout
	 * @private
	 */
	private _getVerifyForm(): DynamicFormControl<any>[][] {
		return [
			[
				new DynamicFormControl<string>({
					value: '',
					key: 'verify',
					label: 'verify.code',
					controlType: 'phone',
					validators: 'required,maxLength:6,minLength:6',
					transform: 'numbersOnly',
					onEnterPressed: this.onSubmit.bind(this),
				}),
			],
		];
	}

	/**
	 * Handles showing toast if the mfa code is invalid
	 * @param resp the PaymentPortalLoginResponse
	 * @private
	 */
	private _onPaymentPortalResponse(resp: PaymentPortalLoginResponse): void {
		const { http_response_code, status } = resp.SERVICE;
		if (status !== ApiStatus.FAILED) {
			return;
		}

		if (http_response_code === '401' && this.verifyCodeSubmitted) {
			this._store$.dispatch(
				showToastAction({
					message: this._locale.get('verify.invalidcode'),
					toastType: ToastType.ERROR,
				})
			);
			this.verifyCodeSubmitted = false;
			return;
		}
	}
}
